import type {
  TReleaseHighlightsDefinition,
} from '../release-highlights.ts';

export const RELEASE_2_5_0: TReleaseHighlightsDefinition = {
  version: '2.5.0',
  highlights: {
    en: [
      {
        headline: 'Choose the colors and density of Qmulus',
        media: {
          type: 'themed-svg',
          fileName: 'theming.svg',
        },
        description: 'Color themes adapt Qmulus to where it is used: light themes suit bright surroundings such as offices, dark themes the darker surroundings of a plant. The density adapts it to the device it is shown on, from a tablet to a computer screen to a TV in the workshop, and lets complex data and screens be shown more densely, so that more fits at once. Choose both under *Settings > User settings > Appearance*.',
      },
      {
        headline: 'Navigate with the arrow keys',
        media: {
          type: 'themed-svg',
          fileName: 'keyboard-navigation.svg',
        },
        description: 'Move through tables, the asset navigation and the app overview with the keyboard. The up and down arrow keys select a row or an entry, `Enter` opens it. The left and right arrow keys turn the pages of a table, shift the date range, or open the previous or next job in *Production Logs*.',
      },
      {
        headline: 'Apps grouped by department, with new icons',
        media: {
          type: 'themed-svg',
          fileName: 'app-grouping.svg',
        },
        description: 'The apps can be grouped by department, e.g. *Production planning* or *Quality assurance*, each with a new, harmonized icon. New installations use this grouping right away. If your company has used Qmulus before, an administrator can switch to it under *Settings > App settings > App overview* by selecting *By department* as *App grouping*.',
      },
      {
        headline: 'Customize PDF reports in the report builder',
        media: {
          type: 'themed-svg',
          fileName: 'report-builder.svg',
        },
        description: 'Set the paper size and language of all PDF reports under *Settings > Report builder*, and adapt single reports, e.g. by choosing the building blocks of the heat treatment report.',
      },
    ],
    de: [
      {
        headline: 'Farben und Dichte von Qmulus wählen',
        media: {
          type: 'themed-svg',
          fileName: 'theming.svg',
        },
        description: 'Farbschemata passen Qmulus an den Ort an, an dem es genutzt wird: Helle Schemata eignen sich für helle Umgebungen wie Büros, dunkle für die dunklere Umgebung einer Werkshalle. Die Dichte passt es an das Gerät an, auf dem es angezeigt wird, vom Tablet über den Computerbildschirm bis zum Fernseher in der Werkstatt, und lässt komplexe Daten und Ansichten dichter darstellen, sodass mehr auf einmal Platz findet. Wählen Sie beides unter *Einstellungen > Benutzereinstellungen > Erscheinungsbild*.',
      },
      {
        headline: 'Mit den Pfeiltasten navigieren',
        media: {
          type: 'themed-svg',
          fileName: 'keyboard-navigation.svg',
        },
        description: 'Bewegen Sie sich mit der Tastatur durch Tabellen, die Anlagennavigation und die App-Übersicht. Die Pfeiltasten nach oben und unten wählen eine Zeile oder einen Eintrag aus, `Enter` öffnet ihn. Die Pfeiltasten nach links und rechts blättern durch die Seiten einer Tabelle, verschieben den Zeitraum oder öffnen in der *Produktionshistorie* die vorherige oder nächste Charge.',
      },
      {
        headline: 'Apps nach Abteilung gruppiert, mit neuen Symbolen',
        media: {
          type: 'themed-svg',
          fileName: 'app-grouping.svg',
        },
        description: 'Die Apps lassen sich nach Abteilung gruppieren, z. B. *Arbeitsvorbereitung* oder *Qualitätssicherung*, jeweils mit einem neuen, einheitlichen Symbol. Neue Installationen verwenden diese Gruppierung sofort. Hat Ihr Unternehmen Qmulus bereits vorher genutzt, kann ein Administrator unter *Einstellungen > App-Einstellungen > App-Übersicht* bei *App-Gruppierung* die Option *Nach Abteilung* wählen.',
      },
      {
        headline: 'PDF-Berichte in den Einstellungen anpassen',
        media: {
          type: 'themed-svg',
          fileName: 'report-builder.svg',
        },
        description: 'Legen Sie unter *Einstellungen > Berichte* Papierformat und Sprache aller PDF-Berichte fest, und passen Sie einzelne Berichte an, z. B. durch die Auswahl der Bausteine des Wärmebehandlungsberichts.',
      },
    ],
    pl: [
      {
        headline: 'Wybierz kolory i gęstość Qmulus',
        media: {
          type: 'themed-svg',
          fileName: 'theming.svg',
        },
        description: 'Motywy kolorystyczne dopasowują Qmulus do miejsca, w którym jest używany: jasne motywy sprawdzają się w jasnym otoczeniu, np. w biurach, a ciemne w ciemniejszym otoczeniu zakładu. Gęstość dopasowuje go do urządzenia, na którym jest wyświetlany, od tabletu, przez ekran komputera, po telewizor w warsztacie, i pozwala wyświetlać złożone dane i ekrany gęściej, tak aby naraz mieściło się więcej. Wybierz obie opcje w *Ustawienia > Ustawienia użytkownika > Wygląd*.',
      },
      {
        headline: 'Nawigacja klawiszami strzałek',
        media: {
          type: 'themed-svg',
          fileName: 'keyboard-navigation.svg',
        },
        description: 'Poruszaj się po tabelach, nawigacji zasobów i przeglądzie aplikacji za pomocą klawiatury. Strzałki w górę i w dół wybierają wiersz lub pozycję, a `Enter` ją otwiera. Strzałki w lewo i w prawo przełączają strony tabeli, przesuwają zakres dat lub otwierają poprzednią albo następną pracę w *Dziennikach produkcji*.',
      },
      {
        headline: 'Aplikacje pogrupowane według działów, z nowymi ikonami',
        media: {
          type: 'themed-svg',
          fileName: 'app-grouping.svg',
        },
        description: 'Aplikacje można pogrupować według działu, np. *Przygotowanie produkcji* lub *Zapewnienie jakości*, a każda z nich ma nową, ujednoliconą ikonę. Nowe instalacje korzystają z tego grupowania od razu. Jeśli Twoja firma korzystała już wcześniej z Qmulus, administrator może je włączyć w *Ustawienia > Ustawienia aplikacji > Przegląd aplikacji*, wybierając *Według działu* w polu *Grupowanie aplikacji*.',
      },
      {
        headline: 'Dostosuj raporty PDF w kreatorze raportów',
        media: {
          type: 'themed-svg',
          fileName: 'report-builder.svg',
        },
        description: 'Ustaw rozmiar papieru i język wszystkich raportów PDF w *Ustawienia > Kreator raportów* i dostosuj poszczególne raporty, np. wybierając bloki raportu z obróbki cieplnej.',
      },
    ],
    sk: [
      {
        headline: 'Vyberte si farby a hustotu Qmulus',
        media: {
          type: 'themed-svg',
          fileName: 'theming.svg',
        },
        description: 'Farebné témy prispôsobia Qmulus miestu, kde sa používa: svetlé témy sa hodia do svetlého prostredia, napr. do kancelárií, tmavé do tmavšieho prostredia výrobnej haly. Hustota ho prispôsobí zariadeniu, na ktorom sa zobrazuje, od tabletu cez obrazovku počítača až po televízor v dielni, a umožní zobraziť zložité údaje a obrazovky hustejšie, aby sa naraz zmestilo viac. Oboje si vyberte v časti *Nastavenia > Nastavenia používateľa > Vzhľad*.',
      },
      {
        headline: 'Navigácia šípkami',
        media: {
          type: 'themed-svg',
          fileName: 'keyboard-navigation.svg',
        },
        description: 'Pohybujte sa po tabuľkách, navigácii zariadení a prehľade aplikácií pomocou klávesnice. Šípky nahor a nadol vyberú riadok alebo položku, `Enter` ju otvorí. Šípky doľava a doprava listujú stránkami tabuľky, posúvajú časový rozsah alebo otvoria predchádzajúcu či nasledujúcu zákazku v *Výrobných záznamoch*.',
      },
      {
        headline: 'Aplikácie zoskupené podľa oddelení, s novými ikonami',
        media: {
          type: 'themed-svg',
          fileName: 'app-grouping.svg',
        },
        description: 'Aplikácie je možné zoskupiť podľa oddelenia, napr. *Príprava výroby* alebo *Zabezpečenie kvality*, každá s novou, zjednotenou ikonou. Nové inštalácie používajú toto zoskupenie hneď. Ak vaša firma používala Qmulus už predtým, administrátor ho môže zapnúť v časti *Nastavenia > Nastavenia aplikácie > Prehľad aplikácií* výberom možnosti *Podľa oddelenia* v poli *Zoskupenie aplikácií*.',
      },
      {
        headline: 'Prispôsobte si PDF správy v tvorcovi správ',
        media: {
          type: 'themed-svg',
          fileName: 'report-builder.svg',
        },
        description: 'Nastavte veľkosť papiera a jazyk všetkých PDF správ v časti *Nastavenia > Tvorca správ* a prispôsobte jednotlivé správy, napr. výberom blokov správy o tepelnom spracovaní.',
      },
    ],
  },
};
